import { useMemo, useState } from 'react'
import { CalendarClock, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { formatMemoryDate } from '@/frontend/components/DigitalWorkers/memory-bank/utils'
import type { MemoryRow } from '@/frontend/components/DigitalWorkers/memory-bank/types'
import type { FdeCopy, ProjectKnowledgePayload } from '@/frontend/features/Fde/types'
import { projectKnowledgeBadges } from '@/frontend/features/Fde/projectKnowledgeTags'

export default function ProjectKnowledgeList({ knowledge, copy, selected, onSelect }: { knowledge: ProjectKnowledgePayload; copy: FdeCopy; selected: MemoryRow | null; onSelect: (row: MemoryRow) => void }) {
  const [query, setQuery] = useState('')
  const rows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR')
    if (!normalized) return knowledge.graph.table_rows
    return knowledge.graph.table_rows.filter((row) => {
      const badges = projectKnowledgeBadges(row.tags).map(({ label }) => label).join(' ')
      return `${row.text} ${row.context || ''} ${row.entities || ''} ${row.tags?.join(' ') || ''} ${badges}`.toLocaleLowerCase('pt-BR').includes(normalized)
    })
  }, [knowledge.graph.table_rows, query])

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="relative border-b border-border p-3">
        <Search className="pointer-events-none absolute left-6 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.material.knowledge_filter} className="h-9 pl-9" />
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="divide-y divide-border">
          {rows.map((row) => <FactRow key={row.id} row={row} active={row.id === selected?.id} onClick={() => onSelect(row)} />)}
          {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">{copy.material.knowledge_no_results}</p>}
        </div>
      </ScrollArea>
    </div>
  )
}

function FactRow({ row, active, onClick }: { row: MemoryRow; active: boolean; onClick: () => void }) {
  const source = (row as MemoryRow & { source?: { path: string } }).source
  const date = row.mentioned_at || row.occurred_start || row.created_at
  const badges = projectKnowledgeBadges(row.tags)
  return (
    <button type="button" onClick={onClick} className={`block w-full px-4 py-3 text-left transition-colors ${active ? 'bg-primary/5' : 'hover:bg-muted/40'}`}>
      <p className="line-clamp-2 text-sm leading-5 text-foreground">{row.text}</p>
      {badges.length > 0 && <span className="mt-1.5 flex flex-wrap gap-1">{badges.map((badge) => <span key={badge.tag} className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${badge.className}`}>{badge.label}</span>)}</span>}
      <span className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground">
        {source?.path && <span className="min-w-0 truncate font-mono">{source.path}</span>}
        {date && <span className="ml-auto flex shrink-0 items-center gap-1"><CalendarClock className="size-3" />{formatMemoryDate(date, false)}</span>}
      </span>
    </button>
  )
}
